import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, Send, Loader2, BookOpen, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { refToHebrew } from "../../lib/hebrewRef";
import Markdown from "../Markdown";
import { toast } from "sonner";

/**
 * ChavrutaPanel — empathetic AI study partner.
 *
 * Behaviour (post empathy upgrade):
 *  • Citations now live INLINE in the assistant's prose ("בתהילים יש
 *    פסוק שאומר…"). The standalone source-card list was removed per
 *    UX request — it broke the conversational feel.
 *  • Verse-anchored context (user tapped a verse) is still passed
 *    to the backend; the LLM weaves it naturally into the reply.
 *  • The "X / 20 שאלות" quota badge stays.
 *  • A small persistent footer note reminds users this is a study
 *    chavruta, not a posek.
 */
export default function ChavrutaPanel({ onClose, consumePendingContext }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const dir = isEn ? "ltr" : "rtl";
  const { user, updateGender } = useAuth();
  // Persist a recent chat for 30 minutes so closing-then-reopening the
  // bubble within a single learning session restores the conversation.
  // Beyond the TTL we start fresh — the chat is an aid, not a journal.
  const STORAGE_KEY = "torahya_chavruta_chat_v1";
  const TTL_MS = 30 * 60 * 1000;
  const loadPersisted = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") return null;
      if (typeof obj.lastActivity !== "number") return null;
      if (Date.now() - obj.lastActivity > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return obj;
    } catch {
      return null;
    }
  };
  const persisted = (typeof window !== "undefined") ? loadPersisted() : null;

  const [messages, setMessages] = useState(persisted?.messages || []);
  const [threadId, setThreadId] = useState(persisted?.threadId || null);
  const [pendingContext, setPendingContext] = useState(null); // { ref, selected_text }
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState(null);
  const [savingGender, setSavingGender] = useState(false);
  // Shabbat/Yom Tov availability — null = unknown (treat as available),
  // {available:true,...} or {available:false,...}. Re-checked every 60s
  // and at the user's next havdalah for auto-unlock without refresh.
  const [availability, setAvailability] = useState(null);
  const scrollerRef = useRef(null);

  // Save the chat to localStorage whenever it changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (messages.length === 0 && !threadId) {
      // Nothing meaningful yet — don't pollute storage.
      return;
    }
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          messages,
          threadId,
          lastActivity: Date.now(),
        }),
      );
    } catch {
      /* quota or private-mode failures are non-fatal */
    }
  }, [messages, threadId]);

  // Current grammatical gender for second-person address. Defaults to
  // masculine (Hebrew's unmarked form) when the user hasn't picked one.
  const effectiveGender = user?.gender === "female" ? "female" : "male";

  const handleToggleGender = useCallback(async () => {
    if (savingGender) return;
    const next = effectiveGender === "female" ? "male" : "female";
    setSavingGender(true);
    try {
      await updateGender(next);
      toast.success(
        next === "female"
          ? t("chavruta_panel.toggle_to_female_done", "מעכשיו פונה אלייך בלשון נקבה")
          : t("chavruta_panel.toggle_to_male_done", "מעכשיו פונה אליך בלשון זכר"),
      );
    } catch (e) {
      toast.error(t("chavruta_panel.save_gender_failed"));
    } finally {
      setSavingGender(false);
    }
  }, [effectiveGender, savingGender, updateGender]);

  // Pick up any context the bubble received via openChavrutaWithContext.
  useEffect(() => {
    const ctx = consumePendingContext?.();
    if (ctx) {
      setPendingContext({
        ref: ctx.ref || null,
        selected_text: ctx.selected_text || null,
      });
      if (ctx.initial_question) setInput(ctx.initial_question);
    }
  }, [consumePendingContext]);

  useEffect(() => {
    (async () => {
      try {
        const q = await api.chavrutaQuota();
        setQuota(q);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  // ── Shabbat / Yom Tov availability ────────────────────────────
  // Poll once on mount, then every 60 seconds, and also when the
  // window regains focus. Returns_at carries the next havdalah —
  // we use it to do a precise wake-up right after havdalah without
  // waiting for the next 60s tick.
  useEffect(() => {
    let timer = null;
    let exactTimer = null;
    let cancelled = false;
    const refresh = async () => {
      try {
        const a = await api.chavrutaAvailability();
        if (cancelled) return;
        setAvailability(a);
        // Schedule an exact re-check 5s after the next havdalah so
        // the input wakes up promptly.
        if (exactTimer) { clearTimeout(exactTimer); exactTimer = null; }
        if (!a.available && a.returns_at) {
          const wakeAt = new Date(a.returns_at).getTime() + 5_000;
          const wait = wakeAt - Date.now();
          if (wait > 0 && wait < 24 * 3600 * 1000) {
            exactTimer = setTimeout(refresh, wait);
          }
        }
      } catch { /* fail-open silently */ }
    };
    refresh();
    timer = setInterval(refresh, 60_000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (exactTimer) clearTimeout(exactTimer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSend = useCallback(async () => {
    const q = input.trim();
    if (!q || sending) return;
    // Block sending during Shabbat / Yom Tov — even if the user
    // somehow bypassed the disabled UI (keyboard, browser extension,
    // stale tab). BE also enforces this; FE check is a courtesy.
    if (availability && availability.available === false) {
      toast.info(availability.label_he || t("chavruta_panel.shabbat_message"), {
        description: t("chavruta_panel.shabbat_desc"),
      });
      return;
    }
    if (quota && quota.remaining <= 0) {
      toast.error(t("chavruta_panel.quota_hit", { limit: quota.limit }));
      return;
    }

    const userMsg = {
      id: `local-${Date.now()}`,
      role: "user",
      content: q,
      ref: pendingContext?.ref || null,
      selected_text: pendingContext?.selected_text || null,
      ts: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);

    try {
      const res = await api.chavrutaAsk({
        question: q,
        thread_id: threadId || undefined,
        ref: pendingContext?.ref || undefined,
        selected_text: pendingContext?.selected_text || undefined,
        lang: i18n.language === "en" ? "en" : "he",
      });
      if (res?.error === "daily_limit_reached") {
        setMessages((m) => [...m, {
          id: `dl-${Date.now()}`,
          role: "assistant",
          content: (isEn && res.message_en) ? res.message_en : res.message_he,
          ts: new Date().toISOString(),
        }]);
        setQuota({ ...(quota || {}), remaining: 0 });
        return;
      }
      if (res?.error === "shabbat_lockout") {
        // Surfaces the same lock our FE polling already shows — keep
        // the user's question text intact so they can resend after
        // havdalah without retyping.
        setInput(q);
        setMessages((m) => m.filter((msg) => msg.id !== userMsg.id));
        setAvailability(res.availability || availability);
        toast.info((isEn && res.message_en) || res.message_he || t("chavruta_panel.shabbat_message"), {
          description: t("chavruta_panel.shabbat_desc"),
        });
        return;
      }
      const assistantMsg = res.message;
      setMessages((m) => [...m, assistantMsg]);
      setThreadId(res.thread_id);
      setQuota(res.quota);
      // Context consumed on the turn it was attached to.
      setPendingContext(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || t("chavruta_panel.send_failed");
      toast.error(detail);
      setMessages((m) => m.filter((msg) => msg.id !== userMsg.id));
    } finally {
      setSending(false);
    }
  }, [input, sending, quota, availability, threadId, pendingContext]);

  return (
    <div
      dir={dir}
      className="
        fixed z-50
        inset-x-0 bottom-0
        sm:inset-x-auto sm:start-4 sm:end-auto sm:bottom-4
        sm:w-[420px] sm:max-w-[calc(100vw-2rem)]
      "
      data-testid="chavruta-panel"
    >
      <div
        className="
          flex flex-col
          bg-card border border-[#c9a449]/40
          rounded-t-2xl sm:rounded-2xl
          shadow-[0_-20px_60px_-15px_rgba(26,43,74,0.4)]
          sm:shadow-[0_20px_60px_-15px_rgba(26,43,74,0.4)]
          h-[88vh] sm:h-[640px] sm:max-h-[80vh]
          overflow-hidden
        "
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div
          className="bg-[#1a2b4a] text-[#f6e9c5] px-4 py-3 flex items-center justify-between gap-3"
          data-testid="chavruta-panel-header"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 text-[#c9a449] shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <h2 className="font-heading text-sm sm:text-base truncate">{t("chavruta_panel.title")}</h2>
              <p className="text-[10px] sm:text-[11px] text-[#f6e9c5]/70 truncate">
                {t("chavruta_panel.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleToggleGender}
              disabled={savingGender}
              data-testid="chavruta-gender-toggle"
              aria-label={
                effectiveGender === "female"
                  ? t("chavruta_panel.toggle_to_male")
                  : t("chavruta_panel.toggle_to_female")
              }
              title={
                effectiveGender === "female"
                  ? t("chavruta_panel.tooltip_female")
                  : t("chavruta_panel.tooltip_male")
              }
              className="
                text-[10px] font-mono text-[#f6e9c5]/85 bg-[#1a2b4a]/60
                border border-[#c9a449]/40 rounded-full px-2 py-0.5
                hover:bg-[#c9a449]/15 hover:border-[#c9a449]/70
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {effectiveGender === "female" ? t("chavruta_panel.pronoun_female") : t("chavruta_panel.pronoun_male")}
            </button>
            {quota && (
              <span
                className="text-[10px] font-mono text-[#f6e9c5]/80 bg-[#c9a449]/10 border border-[#c9a449]/30 rounded-full px-2 py-0.5"
                data-testid="chavruta-quota"
                dir="ltr"
              >
                {quota.remaining}/{quota.limit}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="chavruta-close"
              aria-label={t("chavruta_panel.close")}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white/10"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Conversation */}
        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-4 bg-background"
          data-testid="chavruta-conversation"
        >
          {messages.length === 0 && (
            <EmptyState pendingContext={pendingContext} />
          )}
          {messages.map((m) => (
            <Bubble key={m.id} message={m} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="chavruta-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#c9a449]" strokeWidth={1.5} />
              <span>{t("chavruta_panel.thinking")}</span>
            </div>
          )}
        </div>

        {/* Pending context chip (when verse-anchored) */}
        {pendingContext && (pendingContext.ref || pendingContext.selected_text) && (
          <ContextChip
            context={pendingContext}
            onClear={() => setPendingContext(null)}
          />
        )}

        {/* Input bar — replaced by Shabbat/Yom Tov hero when applicable.
            History above remains fully readable; only NEW questions
            are blocked. The block auto-lifts at havdalah thanks to
            the polling + exact-timer in the availability effect. */}
        {availability && availability.available === false ? (
          <div
            className="border-t border-[#c9a449]/30 bg-card/80 px-4 py-5 text-center space-y-2"
            data-testid="chavruta-shabbat-lock"
          >
            <div className="font-heading text-base text-[#c9a449]" data-testid="chavruta-shabbat-greeting">
              {availability.label_he || t("chavruta_panel.shabbat_message")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-sm mx-auto">
              {t("chavruta_panel.shabbat_desc")}
            </p>
            <textarea
              data-testid="chavruta-input"
              value=""
              readOnly
              rows={1}
              placeholder={t("chavruta_panel.input_placeholder_paused")}
              disabled
              aria-disabled="true"
              className="
                w-full resize-none rounded-xl border border-border/40 bg-background/40
                px-3 py-2 text-sm text-muted-foreground/60
                min-h-[40px]
                cursor-not-allowed
              "
              dir={dir}
            />
          </div>
        ) : (
        <div className="border-t border-border/60 bg-card/80 px-3 py-2 sm:py-3">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="chavruta-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={1}
              placeholder={t("chavruta_panel.input_placeholder_active")}
              maxLength={2000}
              className="
                flex-1 resize-none rounded-xl border border-border/70 bg-background
                px-3 py-2 text-sm focus:outline-none focus:border-[#c9a449]/60
                placeholder:text-muted-foreground/60
                min-h-[40px] max-h-[120px]
              "
              dir={dir}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              data-testid="chavruta-send"
              className="
                h-10 w-10 rounded-full bg-[#1a2b4a] text-[#f6e9c5] border border-[#c9a449]/60
                hover:bg-[#1a2b4a]/90 disabled:opacity-40 disabled:cursor-not-allowed
                inline-flex items-center justify-center shrink-0
              "
              aria-label={t("chavruta_panel.send_aria")}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Send className={`h-4 w-4 ${isEn ? "" : "rotate-180"}`} strokeWidth={1.5} />
              )}
            </button>
          </div>
          <p
            className="mt-1.5 text-[10px] text-muted-foreground/70 text-center leading-snug"
            data-testid="chavruta-footer-disclaimer"
          >
            {t("chavruta_panel.footer_disclaimer")}
          </p>
        </div>
        )}
      </div>
    </div>
  );
}


// --------------------------------------------------------------------- //
// Bubble + supporting bits
// --------------------------------------------------------------------- //

function EmptyState({ pendingContext }) {
  const { t } = useTranslation();
  return (
    <div className="text-center py-8 px-4 space-y-3" data-testid="chavruta-empty-state">
      <Sparkles className="h-10 w-10 mx-auto text-[#c9a449]/60" strokeWidth={1.4} />
      <h3 className="font-heading text-base text-foreground">{t("chavruta_panel.intro_title")}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed max-w-xs mx-auto">
        {t("chavruta_panel.intro_body_1")}
        <br />
        {t("chavruta_panel.intro_body_2")}
      </p>
      {pendingContext && (
        <div className="text-[11px] text-[#c9a449] bg-[#c9a449]/10 border border-[#c9a449]/30 rounded-lg px-3 py-2 inline-block max-w-[260px]">
          {t("chavruta_panel.ready_on")}<strong>{refToHebrew(pendingContext.ref) || t("chavruta_panel.highlighted_text")}</strong>
        </div>
      )}
    </div>
  );
}


function ContextChip({ context, onClear }) {
  const { t } = useTranslation();
  return (
    <div
      className="mx-3 mb-2 px-3 py-1.5 rounded-lg bg-[#c9a449]/10 border border-[#c9a449]/30 flex items-center gap-2 text-[11px]"
      data-testid="chavruta-context-chip"
    >
      <BookOpen className="h-3.5 w-3.5 text-[#c9a449]" strokeWidth={1.5} />
      <span className="flex-1 truncate">
        {t("chavruta_panel.next_question_basedon")}
        {" "}
        <strong className="text-foreground">
          {refToHebrew(context.ref) || t("chavruta_panel.text_you_highlighted")}
        </strong>
      </span>
      <button
        type="button"
        onClick={onClear}
        className="text-muted-foreground hover:text-foreground"
        aria-label={t("chavruta_panel.remove_context_aria")}
        data-testid="chavruta-context-clear"
      >
        <X className="h-3 w-3" strokeWidth={1.5} />
      </button>
    </div>
  );
}


function Bubble({ message }) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const isUser = message.role === "user";

  // Goal: USER bubble visually on the side where the user "types from"
  // — which depends on the script direction. In RTL (HE) the user's
  // text is anchored at the right; in LTR (EN) at the right too (most
  // chat conventions place the user on the trailing edge). To get this
  // with logical alignment values we use start/end relative to dir.
  //   • HE (RTL): user → justify-start (right), assistant → justify-end (left)
  //   • EN (LTR): user → justify-end (right), assistant → justify-start (left)
  // Bubble corner asymmetry follows the same logic so the small corner
  // always points inward toward the speaker.
  const userJustify = isEn ? "justify-end" : "justify-start";
  const assistantJustify = isEn ? "justify-start" : "justify-end";
  const userCornerClass = isEn ? "rounded-tl-md" : "rounded-tr-md";
  const assistantCornerClass = isEn ? "rounded-tr-md" : "rounded-tl-md";

  return (
    <div
      className={`flex ${isUser ? userJustify : assistantJustify}`}
      data-testid={`chavruta-bubble-${message.role}`}
    >
      <div className={`max-w-[88%] sm:max-w-[78%] ${isUser ? "order-1" : "order-2"}`}>
        <div
          className={`
            rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed
            ${isUser
              ? `bg-[#1a2b4a] text-[#f6e9c5] ${userCornerClass} whitespace-pre-wrap`
              : `bg-card border border-border/60 ${assistantCornerClass} text-foreground`
            }
          `}
        >
          {isUser ? message.content : <Markdown className="text-sm" dir={isEn ? "ltr" : "rtl"}>{message.content}</Markdown>}
        </div>

        {/* Context badge on USER messages — which verse the question was anchored to. */}
        {isUser && message.ref && (
          <div className="mt-1 text-[10px] text-muted-foreground/80 flex items-center gap-1">
            <BookOpen className="h-3 w-3" strokeWidth={1.5} />
            <span>{refToHebrew(message.ref)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
